import { financialChange, periodLabel } from '../company-profile-template/financial-report';
import { PeriodTableColumn, PeriodTableRow } from '../company-profile-template/company-profile-period-table.component';

/** Presenters for the Tax profile's recorded history dialogs: patents, grants, and energy and emissions by period. */

export type TaxProfileHistory = 'patents' | 'grants' | 'energy';

export type TaxRecordTone = 'positive' | 'neutral' | 'muted';

export interface TaxPatentRecord {
  title: string;
  applicationNumber?: string;
  publicationNumber?: string;
  status?: string;
  statusTone: TaxRecordTone;
  filed?: string;
  lodged?: string;
  revoked?: string;
  expired?: string;
  applicant?: string;
  url?: string;
}

export interface TaxGrantRecord {
  title: string;
  offered: number | null;
  totalCosts: number | null;
  spendToDate: number | null;
  start?: string;
  end?: string;
  /** From the project dates against today: not started, in progress or ended. */
  timing?: 'Not started' | 'In progress' | 'Ended';
  summary?: string;
}

export interface TaxEnergyTab {
  id: 'energy' | 'emissions';
  label: string;
  /** Toolbar detail after the period count. */
  unitNote: string;
  columns: PeriodTableColumn[];
  rows: PeriodTableRow[];
}

function text(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function isoDate(value: unknown): string | undefined {
  const date = text(value);
  return date && Number.isFinite(Date.parse(date)) ? date : undefined;
}

function amount(value: unknown): number | null {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function safeUrl(value: unknown): string | undefined {
  const url = text(value);
  return /^https?:\/\//i.test(url) ? url : undefined;
}

/** Newest first by the given date; undated records keep their order after the dated ones. */
function newestFirst<T>(items: T[], date: (item: T) => string | undefined): T[] {
  return items.map((item, index) => ({ item, index, time: Date.parse(date(item) || '') }))
    .sort((a, b) => {
      const aDated = Number.isFinite(a.time);
      const bDated = Number.isFinite(b.time);
      if (aDated && bDated && a.time !== b.time) return b.time - a.time;
      if (aDated !== bDated) return aDated ? -1 : 1;
      return a.index - b.index;
    })
    .map(entry => entry.item);
}

const STATUS_ACRONYMS = /^(EPO|PCT|EP|GB|UK|WO|S\d+[A-Z]?|A-PUB)$/i;

/** "TAKEN ON FROM EPO FILE - PCT NO EP SPEC" becomes "Taken on from EPO file - PCT no EP spec". */
export function taxPatentStatusLabel(status: unknown): string | undefined {
  const raw = text(status);
  if (!raw) return undefined;
  const words = raw.split(/\s+/).map(word => STATUS_ACRONYMS.test(word) ? word.toUpperCase() : word.toLowerCase());
  const sentence = words.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

function patentTone(status: string | undefined): TaxRecordTone {
  if (!status) return 'neutral';
  if (/^granted$/i.test(status)) return 'positive';
  return /expired|terminated|revoked|ceased|withdrawn|refused|disposed/i.test(status) ? 'muted' : 'neutral';
}

function applicantName(applicant: any): string | undefined {
  if (!applicant || typeof applicant !== 'object') return undefined;
  return text(applicant.legal_name) ||
    [applicant.prefix, applicant.first_name, applicant.last_name, applicant.suffix].map(text).filter(Boolean).join(' ') || undefined;
}

/** Every patent in patents_filed, newest filing (or lodging) first. */
export function taxPatentHistory(company: Record<string, any>): TaxPatentRecord[] {
  const patents = Array.isArray(company?.patents_filed) ? company['patents_filed'] : [];
  const records: TaxPatentRecord[] = patents.filter((patent: any) => patent && typeof patent === 'object').map((patent: any) => {
    const status = taxPatentStatusLabel(patent.status);
    return {
      title: text(patent.title),
      applicationNumber: text(patent.application_number) || undefined,
      publicationNumber: text(patent.publication_number) || undefined,
      status,
      statusTone: patentTone(status),
      filed: isoDate(patent.date_filed),
      lodged: isoDate(patent.date_lodged),
      revoked: isoDate(patent.date_revoked),
      expired: isoDate(patent.date_expired),
      applicant: applicantName(patent.first_applicant),
      url: safeUrl(patent.ipo_link),
    };
  });
  return newestFirst(records, record => record.filed || record.lodged);
}

/** Strips the quotes and Markdown escapes some grant summaries arrive with, and drops placeholder summaries. */
function grantSummary(value: unknown): string | undefined {
  // Some summaries keep Markdown escapes, such as "\>£50M".
  const summary = text(value).replace(/^"([\s\S]*)"$/, '$1').replace(/\\([\\`*_{}[\]()#+\-.!>])/g, '$1').trim();
  return summary && !/^awaiting public summary$/i.test(summary) ? summary : undefined;
}

function grantTiming(start: string | undefined, end: string | undefined, today: Date): TaxGrantRecord['timing'] {
  const now = today.getTime();
  if (start && Date.parse(start) > now) return 'Not started';
  if (end) return Date.parse(end) < now ? 'Ended' : start ? 'In progress' : undefined;
  return undefined;
}

/** Every grant in grants, newest project start first. */
export function taxGrantHistory(company: Record<string, any>, today = new Date()): TaxGrantRecord[] {
  const grants = Array.isArray(company?.grants) ? company['grants'] : [];
  const records: TaxGrantRecord[] = grants.filter((grant: any) => grant && typeof grant === 'object').map((grant: any) => {
    const start = isoDate(grant.project_start_date);
    const end = isoDate(grant.project_end_date);
    return {
      title: text(grant.project_title),
      offered: amount(grant.grant_offered),
      totalCosts: amount(grant.total_costs),
      spendToDate: amount(grant.spend_to_date),
      start,
      end,
      timing: grantTiming(start, end, today),
      summary: grantSummary(grant.description),
    };
  });
  return newestFirst(records, record => record.start);
}

/** "View all 7 patents", or "View patent details" for a single record. */
export function taxHistoryActionLabel(count: number, noun: 'patent' | 'grant'): string {
  return count > 1 ? `View all ${count} ${noun}s` : `View ${noun} details`;
}

interface EnergyMetric {
  key: string;
  label: string;
  /** Multiplier from the API unit to the shown unit. */
  scale?: number;
  decimals: number;
}

const ENERGY_METRICS: EnergyMetric[] = [
  { key: 'total_energy_kwh', label: 'Total energy use (kWh)', decimals: 0 },
  { key: 'electric_kwh', label: 'Electricity (kWh)', decimals: 0 },
  { key: 'gas_kwh', label: 'Gas (kWh)', decimals: 0 },
  { key: 'scope_1_kwh', label: 'Scope 1 energy (kWh)', decimals: 0 },
  { key: 'scope_2_kwh', label: 'Scope 2 energy (kWh)', decimals: 0 },
  { key: 'scope_3_kwh', label: 'Scope 3 energy (kWh)', decimals: 0 },
];

// The API reports emissions in kg CO2e; tonnes read better at company scale.
const EMISSIONS_METRICS: EnergyMetric[] = [
  { key: 'total_emissions_kg_co2e', label: 'Total emissions (tCO₂e)', scale: .001, decimals: 1 },
  { key: 'scope_1_kg_co2e', label: 'Scope 1 emissions (tCO₂e)', scale: .001, decimals: 1 },
  { key: 'scope_2_kg_co2e', label: 'Scope 2 emissions (tCO₂e)', scale: .001, decimals: 1 },
  { key: 'scope_3_kg_co2e', label: 'Scope 3 emissions (tCO₂e)', scale: .001, decimals: 1 },
  { key: 'kg_co2e_per_million_revenue', label: 'Intensity per £1m revenue (tCO₂e)', scale: .001, decimals: 1 },
  { key: 'kg_co2e_per_employee', label: 'Intensity per employee (tCO₂e)', scale: .001, decimals: 1 },
];

function formatMetric(value: number | null, decimals: number): string {
  if (value === null) return '–';
  const formatted = new Intl.NumberFormat('en-GB', { maximumFractionDigits: decimals, minimumFractionDigits: decimals }).format(Math.abs(value));
  return value < 0 ? `-${formatted}` : formatted;
}

/** One entry per period end, newest first; the API sends newest first, but a stray order or duplicate should not show. */
function periodEntries(entries: unknown): Array<{ period: string; entry: Record<string, any> }> {
  const seen = new Set<string>();
  const list = (Array.isArray(entries) ? entries : [])
    .filter((entry: any) => entry && typeof entry === 'object' && isoDate(entry.made_up_to))
    .map((entry: any) => ({ period: String(entry.made_up_to).slice(0, 10), entry }))
    .filter(({ period }) => !seen.has(period) && !!seen.add(period));
  return newestFirst(list, item => item.period);
}

function energyTab(
  id: TaxEnergyTab['id'], label: string, unitNote: string, entries: unknown, metrics: EnergyMetric[],
  sourceForPeriod: (period: string) => string | undefined,
): TaxEnergyTab {
  const periods = periodEntries(entries);
  const columns: PeriodTableColumn[] = periods.map(({ period }) => {
    const url = sourceForPeriod(period);
    const label = periodLabel(period);
    return {
      key: period, label, prefix: 'Y.E.', url,
      urlLabel: 'Accounts',
      urlAriaLabel: url ? `Open the filed accounts for the period ended ${label} (opens in a new tab)` : undefined,
    };
  });
  const rows: PeriodTableRow[] = metrics.map(metric => {
    const values = periods.map(({ entry }) => {
      const value = amount(entry[metric.key]);
      return value === null ? null : value * (metric.scale ?? 1);
    });
    return {
      name: metric.label,
      type: 'number',
      cells: values.map((value, index) => ({
        value,
        display: formatMetric(value, metric.decimals),
        negative: value !== null && value < 0,
        ...(index < values.length - 1 ? financialChange(value, values[index + 1]) : { change: null, direction: null }),
      })),
    };
  }).filter(row => row.cells.some(cell => cell.value !== null));
  // Only worth a row when some period is flagged, since the flag defaults to false.
  if (id === 'energy' && periods.some(({ entry }) => entry['low_energy'] === true)) {
    rows.push({
      name: 'Low energy user',
      type: 'string',
      cells: periods.map(({ entry }) => {
        const flag = entry['low_energy'];
        const display = flag === true ? 'Yes' : flag === false ? 'No' : '–';
        return { value: typeof flag === 'boolean' ? display : null, display, negative: false, change: null, direction: null };
      }),
    });
  }
  return { id, label, unitNote, columns, rows };
}

/**
 * Energy and emissions as two period tables, one column per reporting period the API returns (newest first), with every
 * recorded metric as a row and the financial report's year-on-year rule. sourceForPeriod links a period to its filed accounts.
 */
export function taxEnergyHistory(company: Record<string, any>, sourceForPeriod: (period: string) => string | undefined = () => undefined): TaxEnergyTab[] {
  const data = company?.energy_and_emissions || {};
  return [
    energyTab('energy', 'Energy', ', newest first · kWh', data.energy, ENERGY_METRICS, sourceForPeriod),
    energyTab('emissions', 'Emissions', ', newest first · tonnes of CO₂ equivalent', data.emissions, EMISSIONS_METRICS, sourceForPeriod),
  ];
}

export function taxEnergyPeriodsText(count: number): string {
  return `${count} reporting ${count === 1 ? 'period' : 'periods'}`;
}
